import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useResolvedUser, useFeatureFlags } from '@tcc/shared-data-lib';
import { useHistory, useLocation } from 'react-router-dom';
import lift from '@tcc/load-lifter';
import { getMessage } from '@tcc/i18n';
import {
  ComponentAlign,
  Container,
  DensitySpacing,
  FdsIcon,
  Orientation,
  ToggleField,
  Tooltip,
} from '@tcc/fds';
import { importFromS3 } from '../../utils/importFromS3';
import {
  isSupportProxySession,
  selectUserByProxyPreference,
  setShouldUseProxyDataSource,
  shouldUseProxyDataSource,
  useShouldUseProxyDataSourcePreference,
  useUserLoggedInConfigs,
  useFeatureFlagsConfigs,
} from '../../utils/sharedData';
import { useNavigationItems } from '../../hooks/useNavigationLinks/useNavigationItems';
import { useNavFooterItems } from '../../hooks/useNavFooterItems';
import { Operation } from '../../hooks/useOperations/types';
import { ALL_OPERATIONS } from '../../utils/operations';
import { shouldHideAllOperations } from '../../utils/shouldHideAllOperations';
import { shouldShowOperationSelector } from '../../utils/shouldShowOperationSelector';
import { shouldShowMultiOperationSelector } from '../../utils/shouldShowMultiOperationSelector';
import { toggleOperationSelectionMode } from '../../utils/toggleOperationSelectionMode';
import { getOperationSelectorDisplayText } from '../../utils/getOperationSelectorDisplayText';
import { HermesLibrary } from '../../types/Hermes';
import { useUserSubMenu } from '../../hooks/useUserSubMenu';
import { useSideNavFirstTour } from '../../hooks/useSideNavFirstTour';


interface IProps {
  expanded: boolean;
  operations: Operation[];
  operationsSelected: Operation[];
  setExpandedState: (isExpanded: boolean) => void;
  setSelectedOperations: (operationSelected: Operation[]) => void;
}

const S3Plugin = React.lazy(() => importFromS3('information-architecture'));
const MFPlugin = React.lazy(() => import('informationArchitecture/InformationArchitecture'));

export const InfoArchitecturePluginContainer: React.FC<IProps> = ({
  expanded,
  operations,
  operationsSelected,
  setExpandedState,
  setSelectedOperations,
}: IProps) => {
  const { navigationItems } = useNavigationItems();
  const {
    user,
    loggedInUser,
    impersonatedUser,
    isProxying,
  } = useResolvedUser(useUserLoggedInConfigs);
  const details = user?.details;
  const shouldUseProxyDataSourcePreference = useShouldUseProxyDataSourcePreference();
  const selectedSharedDataUser = useMemo(
    () => selectUserByProxyPreference(
      {
        user,
        loggedInUser,
        impersonatedUser,
        isProxying,
      },
      shouldUseProxyDataSourcePreference,
    ),
    [
      user,
      loggedInUser,
      impersonatedUser,
      isProxying,
      shouldUseProxyDataSourcePreference,
    ],
  );
  const { featureFlags } = useFeatureFlags(useFeatureFlagsConfigs(selectedSharedDataUser));
  const useModuleFederation = featureFlags?.['web.sideNav.module-federation'];

  const InformationArchitecturePlugin = useModuleFederation ? MFPlugin : S3Plugin;

  const [areNotificationsLoading, setNotificationsLoading] = useState(true);
  const [useProxyDataSource, setUseProxyDataSource] = useState<boolean>(() => shouldUseProxyDataSource());
  const history = useHistory();
  const location = useLocation();
  useSideNavFirstTour();

  const hideAllOperations = useMemo(() => shouldHideAllOperations(location.pathname), [location.pathname]);
  const filteredOperations = useMemo(() =>
    hideAllOperations
      ? operations.filter(op => op.operationId !== ALL_OPERATIONS)
      : operations,
  [hideAllOperations, operations]);

  useEffect(() => {
    if (user && operationsSelected.length > 0) {
      lift(['tcc-hermes'], (Hermes: HermesLibrary) => {
        setNotificationsLoading(false);
        Hermes.install(
          null,
          {
            containerSelector: '#hermesButton',
            insideDashboard: false,
          },
          user.details,
          operationsSelected[0].operationId
        );
      });
    }
  }, [user, operationsSelected]);

  const userInfo = {
    fullName: `${details?.firstName} ${details?.lastName}`,
    uuid: details?.uuid,
    email: details?.email,
  };
  const hasOperationDropdown = shouldShowOperationSelector({
    user,
    route: location.pathname,
    featureFlags: featureFlags || null,
  }) && filteredOperations.length !== 0;

  const hasMultiOperationEnabled = shouldShowMultiOperationSelector(location.pathname, featureFlags || null);

  useEffect(() => {
    setUseProxyDataSource(shouldUseProxyDataSourcePreference);
  }, [shouldUseProxyDataSourcePreference]);


  useEffect(() => {
    toggleOperationSelectionMode({
      hasMultiOperationEnabled,
      operationsSelected,
      operations,
      setSelectedOperations,
      hideAllOperations,
    });
    // Only run when these change
  }, [hasMultiOperationEnabled, operationsSelected, setSelectedOperations, hideAllOperations, operations]);

  const operationsSelectedDisplayMessage = getOperationSelectorDisplayText({
    operations,
    operationsSelected
  });

  const subMenus = useUserSubMenu({ featureFlags, user });
  const showSupportProxyDataSourceToggle = isSupportProxySession(loggedInUser, Boolean(isProxying));
  const supportProxyDataSourceTooltip = getMessage('sidenav.proxyDataSource.toggle.tooltip');
  const userProfileMenuTrailingSlot = showSupportProxyDataSourceToggle
    ? (
      <Container
        data-test-id="support-proxy-data-source-toggle-row"
        orientation={Orientation.Horizontal}
        align={ComponentAlign.Center}
        gap={DensitySpacing.Together}
        width="fit-content"
      >
        <ToggleField
          testId="support-proxy-data-source-toggle"
          label={getMessage('sidenav.proxyDataSource.toggle.label')}
          checked={useProxyDataSource}
          checkedText={getMessage('sidenav.proxyDataSource.toggle.proxyUser')}
          uncheckedText={getMessage('sidenav.proxyDataSource.toggle.loggedInUser')}
          onCheckedChange={(checked) => {
            const nextValue = Boolean(checked);
            setUseProxyDataSource(nextValue);
            setShouldUseProxyDataSource(nextValue);
          }}
        />
        <Tooltip
          testId="support-proxy-data-source-tooltip"
          triggerIcon={FdsIcon.Info}
        >
          {supportProxyDataSourceTooltip}
        </Tooltip>
      </Container>
    )
    : undefined;

  const navFooterItems = useNavFooterItems({
    expanded,
    setExpandedState,
    countryCode: details?.countryCode,
    featureFlags,
  });

  const onLogoClick = useCallback(() => {
    history.push(`/${location.search}`);
  }, [history, location]);

  return (
    <InformationArchitecturePlugin
      allOperationsDisplayMessage={getMessage('sidenav.allOperations')}
      areNotificationsLoading={areNotificationsLoading}
      expanded={expanded}
      hasOperationDropdown={hasOperationDropdown}
      hasMultiOperationEnabled={hasMultiOperationEnabled}
      navigationItems={navigationItems}
      navFooterItems={navFooterItems}
      onLogoClick={onLogoClick}
      operations={filteredOperations}
      operationsSelected={operationsSelected}
      operationsSelectedDisplayMessage={operationsSelectedDisplayMessage}
      setExpandedState={setExpandedState}
      setSelectedOperations={setSelectedOperations}
      subMenusUserProfile={subMenus}
      userProfileMenuTrailingSlot={userProfileMenuTrailingSlot}
      userInfo={userInfo}
    />
  );
};
